import styles from './styles/FeatureTable.css'
import Header from '../typography/Header'
import classNames from 'classnames'
import Tooltip from '../misc/Tooltip'
import PlanData from '../../../config/static/pricing-plans'
import Button from '../forms/Button'
import { toggleModal } from '../../store/ui/actions'
import { useDispatch, useSelector } from 'react-redux'
import { ctaCopy } from './Plan'
import { useEffect, useState } from 'react'
import CircleSpinner from '../misc/CircleSpinner'
import Toggle from '../forms/Toggle'
import Link from '../misc/Link'
import * as analytics from 'lib/analytics'
import { useLocation } from 'react-router-dom'
import qs from 'query-string'
import Paragraph from '../typography/Paragraph'
import CodeSnippet from '../misc/CodeSnippet'
import Modal from '../misc/Modal'

const AnnualToggle = ({ annual, setAnnual }) => (
  <div className={styles.annualToggle}>
    <div className={classNames(styles.option, { [styles.selected]: !annual })} onClick={() => setAnnual(false)}>Pay monthly</div>
    <Toggle
      checked={annual}
      onChange={() => setAnnual(!annual)}
    />
    <div
      className={classNames(styles.option, { [styles.selected]: annual })} onClick={() => setAnnual(true)}
    >Pay annually (Save up to 33% off)
    </div>
  </div>
)

const calculatePrice = (price, percentOff) => {
  if (percentOff) {
    return (price * (1 - percentOff / 100)).toFixed(2)
  }
  return price.toFixed(2)
}

const features = [
  { name: 'Citation Statement Search', tooltipCopy: `Search over ${CONFIG.defaultCitationsTotalText} citation statements to find expert analyses and opinions on all subjects`, features: { basic: false, premium: false, premium_plus: true } },
  { name: 'Citation Statement Alerts', tooltipCopy: 'Stay up to date on when any topic or keyword is mentioned in the text of new publications', features: { basic: false, premium: false, premium_plus: true } },
  { name: 'scite Analyses', tooltipCopy: 'Generate high quality PDF analyses using Citation Statement Search results to provide an overview of prominent researchers, institutions, and publications in any area of research', features: { basic: false, premium: false, premium_plus: true } },
  { name: 'Reference Check', tooltipCopy: 'Upload a manuscript to understand the quality of its references', features: { basic: false, premium: true, premium_plus: true } },
  { name: 'CSV Export', tooltipCopy: 'Export data from scite reports and dashboards and do more advanced analysis', features: { basic: false, premium: true, premium_plus: true } },
  { name: 'New Citation and Publication Alerts', tooltipCopy: 'Stay up to date by setting an alert for when a paper, author, institution, journal, search or custom dashboard receives new citations or publications', features: { basic: false, premium: true, premium_plus: true } },
  { name: 'scite Reports', tooltipCopy: 'See how over 100m publications have been supported, replicated, disputed, criticized, debunked and more', features: { basic: '100/month (all report features included)', premium: 'Unlimited', premium_plus: 'Unlimited' } },
  { name: 'Visualizations', tooltipCopy: 'Discover how articles cite each other', features: { basic: '100/month (all visualization features included)', premium: 'Unlimited', premium_plus: 'Unlimited' } },
  { name: 'Custom Dashboards', tooltipCopy: 'Evaluate any group of publications with custom dashboards', features: { basic: '1 Dashboard (with up to 100 publications)', premium: 'Unlimited Dashboards (with up to 1,000 publications each)', premium_plus: 'Unlimited Dashboards (with up to 1,000 publications each)' } },
  { name: 'Advanced Search', tooltipCopy: 'Find the highest quality publications by searching for literature based on supporting and contrasting citations they have received', features: { basic: 'Order results and search based on title, abstract, authors, journals, and publication year', premium: 'Also includes aggregation filters for top authors, editorial notices, affiliations, and publication types', premium_plus: 'Includes access to Citation Statement Search' } },
  { name: 'Search for Supported and Contrasted Papers', tooltipCopy: 'Search for publications to understand how they have been cited', features: { basic: true, premium: true, premium_plus: true } },
  { name: 'Journal and Institution Dashboards', tooltipCopy: 'Evalute the quality of journals, funders, and institutions research outputs', features: { basic: true, premium: true, premium_plus: true } },
  { name: 'Email and Chat Support', tooltipCopy: 'Email us or send us a chat message at anytime and we will get back to you', features: { basic: true, premium: true, premium_plus: true } },
  { name: 'Help Center', tooltipCopy: 'Find out the best way scite can help you', features: { basic: true, premium: true, premium_plus: true } },
  { name: 'scite Browser Extension', tooltipCopy: 'See how papers have been cited anywhere you are reading research online', features: { basic: true, premium: true, premium_plus: true } },
  { name: 'scite Zotero Plugin', tooltipCopy: 'See how publications in your Zotero library have been cited by others with our Smart Citation data', features: { basic: true, premium: true, premium_plus: true } },
  { name: 'Custom Authentication and Integration', tooltipCopy: 'Integrate scite into your organization', features: { basic: 'With an organization license', premium: 'With an organization license', premium_plus: 'With an organization license' } },
  { name: 'Loginless usage', tooltipCopy: 'Let your users use scite without needed to register or login', features: { basic: 'With an organization license', premium: 'With an organization license', premium_plus: 'With an organization license' } },
  { name: 'COUNTER compliant usage statistics', tooltipCopy: 'Understand how your users are engaging with scite features', features: { basic: 'With an organization license', premium: 'With an organization license', premium_plus: 'With an organization license' } },
  { name: 'Bibliometrics and Research Support', tooltipCopy: 'Get help with research evaluation', features: { basic: 'With an organization license', premium: 'With an organization license', premium_plus: 'With an organization license' } },
  { name: 'Webinars, Training Material, and Priority Support', tooltipCopy: 'Get an overview of different scite features and training on how to improve your research quality from the scite team', features: { basic: 'With an organization license', premium: 'With an organization license', premium_plus: 'With an organization license' } }
]

const getPriceID = (annual, plan) => {
  if (plan === 'basic') {
    return annual ? CONFIG.stripe.basicPlanAnnuallyPriceID : CONFIG.stripe.basicPlanMonthlyPriceID
  }

  if (plan === 'premium') {
    return annual ? CONFIG.stripe.premiumPlanAnnuallyPriceID : CONFIG.stripe.premiumPlanMonthlyPriceID
  }

  if (plan === 'premium_plus') {
    return annual ? CONFIG.stripe.premiumPlusPlanAnnuallyPriceID : CONFIG.stripe.premiumPlusPlanMonthlyPriceID
  }
}

const ShareASalePixel = ({ orderID }) => {
  const location = useLocation()
  const query = qs.parse(location.search)
  if (query.session_id && query.amount && CONFIG.sciteFrontendURL === 'https://scite.ai') {
    return (
      <img src={`https://www.shareasale.com/sale.cfm?tracking=${orderID}&amount=${query.amount}&merchantID=113325&transtype=sale`} width='1' height='1' />
    )
  }
  return null
}

const DiscountCodeModal = ({ discount, show, setShow }) => (
  <Modal className={styles.modal} show={show} onHide={() => setShow(false)} toggle={() => setShow(!show)}>
    <Header level={3}>Redeem Discount</Header>
    <Paragraph>
      Enter or copy the code below and use in your billing portal to redeem your discount.
    </Paragraph>
    <CodeSnippet snippet={discount} />
    <form action={CONFIG.stripe.billingPortalURL} method='POST'>
      <Button
        className={styles.cta}
        color='blue'
        type='submit'
        onClick={() => setShow(false)}
      >
        Proceed to Billing Portal
      </Button>
    </form>
  </Modal>
)

const FeatureTable = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const user = useSelector(({ ui }) => ui.user)
  const [annual, setAnnual] = useState(true)
  const [loading, setLoading] = useState(false)
  const [success, setSucess] = useState(false)

  // a state for modal with discount display
  const [showDiscountModal, setShowDiscountModal] = useState(false)
  const [discount, setDiscount] = useState(null)

  useEffect(() => {
    const query = qs.parse(location.search)
    if (query.session_id && query.plan && query.annual === 'true') {
      setSucess(true)
      analytics.gtag('event', 'purchase', {
        currency: 'USD',
        transaction_id: Date.now(),
        value: query.amount,
        items: [{
          name: (query.plan || '') + ` ${annual ? 'annual' : 'monthly'}`,
          price: query.amount,
          quantity: 1,
          currency: 'USD'
        }]
      })
      analytics.event({
        category: 'User',
        action: 'Purchased Plan',
        label: query.plan
      })
    }
  }, [location])

  const handleClick = (plan, redeemStudentDiscount, studentDiscount) => (e) => {
    if (!user) {
      e.preventDefault()
      dispatch(toggleModal('login'))
      return
    }
    setLoading(true)

    // Since users will be pressing the back button
    // and that won't trigger normal mounting/unmounting behaviour
    // we need to clear this on timeout...
    setTimeout(() => {
      setLoading(false)
    }, 5000)

    analytics.event({
      category: 'User',
      action: 'View Checkout',
      label: plan
    })

    if (redeemStudentDiscount) {
      analytics.event({
        category: 'User',
        action: 'Redeem Student Discount',
        label: plan
      })

      if (user?.subscription?.customerID && user?.subscription?.status === 'active') {
        setDiscount(studentDiscount)
        setShowDiscountModal(true)
        e.preventDefault()
      }
    }

    const planInfo = PlanData[plan]

    analytics.gtag('event', 'view_item', {
      currency: 'USD',
      value: parseFloat(annual ? planInfo.annualPrice : planInfo.monthlyPrice) || 0,
      items: [{
        name: plan + ` ${annual ? 'annual' : 'monthly'}`,
        price: parseFloat(annual ? planInfo.annualPrice : planInfo.monthlyPrice) || 0,
        quantity: 1,
        currency: 'USD'
      }]
    })
  }

  const checkoutURL = user?.subscription?.customerID && user?.subscription?.status === 'active' ? CONFIG.stripe.billingPortalURL : CONFIG.stripe.checkoutSessionURL
  return (
    <div className={styles.featureTableContainer}>
      {showDiscountModal && discount && <DiscountCodeModal discount={discount} show={showDiscountModal} setShow={setShowDiscountModal} />}
      <AnnualToggle annual={annual} setAnnual={setAnnual} />
      {success && user?.subscription?.subID && (
        <ShareASalePixel
          orderID={user?.subscription?.subID}
        />
      )}
      <table className={styles.featureTable}>
        <thead>
          <tr>
            <th className={styles.tableHeader}>
              <Header level={5}> Features </Header>
              <div className={styles.featureHover}>
                *Hover over feature to learn more
              </div>
            </th>
            <th className={classNames(styles.tableHeader, styles.basicTableHeader)}>
              <div className={styles.iconContainer}><img alt='plan-illustration' className={styles.icon} src={`${CONFIG.assetsBaseURL}/images/pricing/${PlanData.basic.illustration}.svg`} /></div>
              <Header level={4} className={styles.header}>Basic</Header>
              <div className={styles.pricing}>
                {annual ? `$${PlanData.basic.price}` : `$${PlanData.basic.monthlyPrice}`}
              </div>

              <div className={styles.subheading}>
                {annual ? 'USD / month, Billed annually' : 'USD / month, Billed monthly'}
              </div>
              <form action={checkoutURL} method='POST'>
                <input type='hidden' name='priceId' value={getPriceID(annual, 'basic')} />
                <input type='hidden' name='plan' value='basic' />
                <input type='hidden' name='annual' value={annual ? 'true' : 'false'} />
                <Button
                  className={styles.cta}
                  color='blue'
                  onClick={handleClick('basic', false)}
                >
                  {loading
                    ? <CircleSpinner size={10} color='white' />
                    : ctaCopy(
                      'basic', user, 'Upgrade Now'
                    )}
                </Button>
              </form>
            </th>
            <th className={classNames(styles.tableHeader, styles.premiumTableHeader)}>

              <img loading='lazy' alt='ribbon' className={styles.ribbon} src={`${CONFIG.assetsBaseURL}/images/pricing/popular-ribbon.svg?v=2`} />

              <div className={styles.iconContainer}><img alt='plan-illustration' className={styles.icon} src={`${CONFIG.assetsBaseURL}/images/pricing/${PlanData.premium.illustration}.svg`} /></div>
              <Header level={4} className={styles.header}>Premium</Header>
              <div className={styles.pricing}>
                {annual ? `$${PlanData.premium.price}` : `$${PlanData.premium.monthlyPrice}`}
              </div>

              <div className={styles.subheading}>
                {annual ? 'USD / month, Billed annually' : 'USD / month, Billed monthly'}
              </div>

              <form action={checkoutURL} method='POST'>
                <input type='hidden' name='priceId' value={getPriceID(annual, 'premium')} />
                <input type='hidden' name='plan' value='premium' />
                <input type='hidden' name='annual' value={annual ? 'true' : 'false'} />
                <Button
                  className={styles.cta}
                  color='blue'
                  onClick={handleClick('premium', false)}
                >
                  {loading
                    ? <CircleSpinner size={10} color='white' />
                    : ctaCopy(
                      'premium', user, 'Upgrade Now'
                    )}
                </Button>
              </form>
            </th>
            <th className={classNames(styles.tableHeader, styles.premiumPlusTableHeader)}>
              <div className={styles.iconContainer}><img alt='plan-illustration' className={styles.icon} src={`${CONFIG.assetsBaseURL}/images/pricing/${PlanData.premium_plus.illustration}.svg`} /></div>
              <Header level={4} className={styles.header}>Premium Plus</Header>
              <div className={styles.pricing}>
                {annual ? `$${PlanData.premium_plus.price}` : `$${PlanData.premium_plus.monthlyPrice}`}
              </div>

              <div className={styles.subheading}>
                {annual ? 'USD / month, Billed annually' : 'USD / month, Billed monthly'}
              </div>

              <form action={checkoutURL} method='POST'>
                <input type='hidden' name='priceId' value={getPriceID(annual, 'premium_plus')} />
                <input type='hidden' name='plan' value='premium_plus' />
                <input type='hidden' name='annual' value={annual ? 'true' : 'false'} />
                <Button
                  className={styles.cta}
                  color='blue'
                  onClick={handleClick('premium_plus', false)}
                >
                  {loading
                    ? <CircleSpinner size={10} color='white' />
                    : ctaCopy(
                      'premium_plus', user, 'Upgrade Now'
                    )}
                </Button>
              </form>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr key='Student and Academic Discounts Available'>
            <td className={styles.tableData}>
              <Tooltip content='Get 30%, 40%, or 50% off if you are a student or academic. Click redeem coupon below to get the coupon.'>
                <strong>Student and Academic Pricing</strong>
              </Tooltip>
            </td>
            <td className={classNames(styles.tableData, styles.centerCell, {
              [styles.bold]: true
            })}
            >{annual ? `$${calculatePrice(PlanData.basic.price, 30)}` : `$${calculatePrice(PlanData.basic.monthlyPrice, 30)}`} (30% off - students only)
              <div className={styles.subheading}>
                {annual ? 'USD / month, Billed annually' : 'USD / month, Billed monthly'}
                <br />
                <form action={checkoutURL} method='POST'>
                  <input type='hidden' name='priceId' value={getPriceID(annual, 'basic')} />
                  <input type='hidden' name='coupon' value={CONFIG.stripe.studentBasicPromotionCode} />
                  <input type='hidden' name='plan' value='basic' />
                  <input type='hidden' name='annual' value={annual ? 'true' : 'false'} />
                  <button type='submit' className={styles.redeem} onClick={handleClick('basic', true, CONFIG.stripe.studentBasicPromotionCode)}><Link blue underline>Redeem Discount</Link></button>
                </form>
              </div>
            </td>
            <td className={classNames(styles.tableData, styles.centerCell, {
              [styles.bold]: true
            })}
            >{annual ? `$${calculatePrice(PlanData.premium.price, 40)}` : `$${calculatePrice(PlanData.premium.monthlyPrice, 40)}`} (40% off - students only)
              <div className={styles.subheading}>
                {annual ? 'USD / month, Billed annually' : 'USD / month, Billed monthly'}
                <br />
                <form action={checkoutURL} method='POST'>
                  <input type='hidden' name='priceId' value={getPriceID(annual, 'premium')} />
                  <input type='hidden' name='plan' value='premium' />
                  <input type='hidden' name='annual' value={annual ? 'true' : 'false'} />
                  <input type='hidden' name='coupon' value={CONFIG.stripe.studentPremiumPromotionCode} />
                  <button type='submit' className={styles.redeem} onClick={handleClick('premium', true, CONFIG.stripe.studentPremiumPromotionCode)}><Link blue underline>Redeem Discount</Link></button>
                </form>
              </div>
            </td>
            <td className={classNames(styles.tableData, styles.centerCell, {
              [styles.bold]: true
            })}
            > {annual ? `$${calculatePrice(PlanData.premium_plus.price, 50)}` : `$${calculatePrice(PlanData.premium_plus.monthlyPrice, 50)}`} (50% off)
              <div className={styles.subheading}>
                {annual ? 'USD / month, Billed annually' : 'USD / month, Billed monthly'}
                <br />
                <form action={checkoutURL} method='POST'>
                  <input type='hidden' name='priceId' value={getPriceID(annual, 'premium_plus')} />
                  <input type='hidden' name='plan' value='premium_plus' />
                  <input type='hidden' name='annual' value={annual ? 'true' : 'false'} />
                  <input type='hidden' name='coupon' value={CONFIG.stripe.studentPremiumPlusPromotionCode} />
                  <button type='submit' className={styles.redeem} onClick={handleClick('premium_plus', true, CONFIG.stripe.studentPremiumPlusPromotionCode)}><Link blue underline>Redeem Discount</Link></button>
                </form>
              </div>
            </td>
          </tr>
          {features.map(feature => (
            <tr key={feature.name}>
              <td className={classNames(styles.tableData, {
                [styles.bold]: feature.bold
              })}
              >
                <Tooltip content={feature.tooltipCopy}>
                  {feature.name}
                </Tooltip>
              </td>
              <td className={classNames(styles.tableData, styles.centerCell, {
                [styles.bold]: feature.bold
              })}
              >{typeof feature.features.basic !== 'boolean' ? feature.features.basic : (feature.features.basic) ? <i className={styles.checkmark} /> : <i className={styles.cross} />}
              </td>
              <td className={classNames(styles.tableData, styles.centerCell, {
                [styles.bold]: feature.bold
              })}
              >{typeof feature.features.premium !== 'boolean' ? feature.features.premium : (feature.features.premium) ? <i className={styles.checkmark} /> : <i className={styles.cross} />}
              </td>
              <td className={classNames(styles.tableData, styles.centerCell, {
                [styles.bold]: feature.bold
              })}
              >{typeof feature.features.premium_plus !== 'boolean' ? feature.features.premium_plus : (feature.features.premium_plus) ? <i className={styles.checkmark} /> : <i className={styles.cross} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default FeatureTable
